<script setup lang="ts">
import { ComputedRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Link, LinkProps } from '@pohlcon/design-system';
import { MessageSchema } from '~/locales';

const { t, locale } = useI18n < { message: MessageSchema }>();

const legals: ComputedRef<{ label: string, link?: string, target?: string; }[]> = computed(() => ([
  { label: t('legalNotes.copyright') + ' © PohlCon GmbH' },
  {
    label: t('legalNotes.termsOfUse'),
    link: import.meta.env.VITE_WEBAPP_HOST.includes('localhost')
      ? 'https://webapp.stage.pohlcondigital.com/terms-of-use'
      : `${import.meta.env.VITE_WEBAPP_HOST}/terms-of-use`,
    target: '_blank'
  },
  // { label: t('legalNotes.dataPolicy'), link: `${import.meta.env.VITE_BASE_URL}/data-policy`, target: '_blank' },
  {
    label: t('legalNotes.dataPolicy'),
    link: locale.value.includes('de')
      ? 'https://pohlcon.com/datenschutz'
      : 'https://pohlcon.com/en/privacy',
    target: '_blank'
  },
  {
    label: t('legalNotes.imprint'),
    link: locale.value.includes('de')
      ? 'https://www.pohlcon.com/impressum'
      : 'https://pohlcon.com/en/imprint',
    target: '_blank'
  },
  { label: t('legalNotes.licenses'), link: '/licenses' }
]));
</script>

<template>
  <footer id="footer">
    <slot />
    <div class="page__footer">
      <div class="legal-pages">
        <template v-for="({ label, link, target }, i) in legals" :key="label">
          <Link
            v-if="link"
            :to="link"
            :text="label"
            :target="(!!target ? target : '_self') as LinkProps['target']"
            :type="'tertiary'"
            class="print__hide"
          />
          <span v-else>{{ label }}</span>
          <div v-if="i < legals.length - 1" class="legal-pages__divider">|</div>
        </template>
      </div>
    <span class="brand-name">H-BAU Technik</span>
  </div>
</footer>
</template>

<style lang="scss" scoped>
@use '@pohlcon/design-system/index.scss' as *;

#footer {
    @extend %hint-2;
    border-top: 1px solid var(--color-grey-80);
    margin-top: var(--gutter-2xl);
    padding-top: var(--gutter-xs);
    color: var(--bds-tertiaryText-color);
    display: flex;
    justify-content: flex-end;
    gap: var(--gutter-xs);

    .welcome-modal__footer {
      border-top: unset;
      margin-top: unset;
      padding-top: unset;
    }
  }

.page__footer {
  @extend %hint-2;
  white-space: nowrap;
  color: var(--bds-tertiaryText-color);
  display: flex;
  align-items: flex-end;
  flex-direction: column;

  .legal-pages {
    display: flex;
    align-items: flex-start;
    column-gap: var(--gutter-2xs);

    :deep(a.lnk) {
      height: min-content;
    }

    &__divider {
      height: 80%;
      margin: auto;
    }
  }

  .brand-name {
    color: var(--color-grey-70);
    text-align: right;
  }
}
</style>
